import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { Spinner, Avatar, seqStatusColor, stepStatusColor, stepStatusLabel, fmtInTz, tzAbbr, MY_TZ } from "../components/ui-bits";
import { ActionMenu, ConfirmDialog } from "../components/crud";
import { SequenceForm } from "../components/record-forms";
import { useToast } from "../hooks/use-toast";
import {
  ArrowLeft, Send, Play, Pause, Plus, Mail, Clock, Check, CheckCheck, X, Loader2,
  Sparkles, RotateCcw, Reply, CalendarClock, Search,
} from "lucide-react";

const enrStatusColor = {
  active: "bg-emerald-100 text-emerald-700",
  replied: "bg-blue-100 text-blue-700",
  paused: "bg-amber-100 text-amber-700",
  completed: "bg-[hsl(240,5%,93%)] text-[hsl(240,4%,45%)]",
};


function EnrollModal({ sequenceId, enrolledIds, onClose, onEnrolled }) {
  const { toast } = useToast();
  const [people, setPeople] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [drafting, setDrafting] = useState(false);

  useEffect(() => { api.people().then(setPeople); }, []);

  const enroll = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const enr = await api.enroll(sequenceId, selected);
      toast({ title: "Hermes drafted the sequence", description: `${enr.steps.length} personalized steps ready to review` });
      onEnrolled(enr);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not enroll", variant: "destructive" });
      setDrafting(false);
    }
  };

  const available = people.filter((p) => !enrolledIds.includes(p.id) && p.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={drafting ? undefined : onClose}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-[hsl(240,6%,90%)] animate-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[hsl(240,6%,92%)]">
          <h3 className="text-[15px] font-semibold">Enroll a person</h3>
          {!drafting && <button onClick={onClose} className="p-1 rounded hover:bg-[hsl(240,5%,95%)]"><X className="h-4 w-4" /></button>}
        </div>
        {drafting ? (
          <div className="p-10 flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-xl brand-bg-soft flex items-center justify-center mb-4"><Sparkles className="h-6 w-6 text-[hsl(245,50%,48%)] animate-pulse" /></div>
            <div className="text-[14px] font-medium">Hermes is drafting a personalized cadence…</div>
            <div className="text-[12.5px] text-[hsl(240,4%,50%)] mt-1">Analyzing profile, picking send days, and writing each touch.</div>
            <Loader2 className="h-5 w-5 animate-spin text-[hsl(245,58%,60%)] mt-4" />
          </div>
        ) : (
          <>
            <div className="p-4">
              <div className="flex items-center gap-1.5 rounded-md border border-[hsl(240,6%,90%)] px-2 py-[6px] mb-2">
                <Search className="h-[15px] w-[15px] text-[hsl(240,4%,55%)]" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people…" className="w-full bg-transparent text-[13px] outline-none" />
              </div>
              <div className="max-h-[300px] overflow-auto -mx-1">
                {available.length === 0 && <div className="text-[13px] text-[hsl(240,4%,55%)] px-2 py-6 text-center">No available people.</div>}
                {available.map((p) => (
                  <button key={p.id} onClick={() => setSelected(p.id)} className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left ${selected === p.id ? "bg-[hsl(245,58%,96%)]" : "hover:bg-[hsl(240,5%,96%)]"}`}>
                    <Avatar text={p.avatar} brand size={28} />
                    <div className="min-w-0 flex-1"><div className="text-[13px] font-medium">{p.name}</div><div className="text-[12px] text-[hsl(240,4%,50%)]">{p.job_title} · {p.company}</div></div>
                    {selected === p.id && <Check className="h-4 w-4 text-[hsl(245,50%,48%)]" />}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-[hsl(240,6%,92%)]">
              <button onClick={onClose} className="rounded-md px-3 py-1.5 text-[13px] font-medium text-[hsl(240,4%,40%)] hover:bg-[hsl(240,5%,95%)]">Cancel</button>
              <button disabled={!selected} onClick={enroll} className="rounded-md brand-bg text-white px-3 py-1.5 text-[13px] font-medium disabled:opacity-50 hover:brightness-105 inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Draft with Hermes</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StepCard({ step, enrollmentId, onChange }) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [schedEditing, setSchedEditing] = useState(false);
  const [subject, setSubject] = useState(step.subject);
  const [body, setBody] = useState(step.body);
  const [date, setDate] = useState(step.scheduled_date || "");
  const [hour, setHour] = useState(step.send_hour_local ?? 9);
  const [busy, setBusy] = useState(false);

  const doAction = async (fn, label) => {
    setBusy(true);
    try { const e = await fn(); onChange(e); if (label) toast({ title: label }); }
    finally { setBusy(false); }
  };
  const saveEdit = async () => {
    const e = await api.editStep(enrollmentId, step.id, { subject, body });
    onChange(e); setEditing(false); toast({ title: "Draft updated" });
  };
  const saveSched = async () => {
    const e = await api.editStep(enrollmentId, step.id, { scheduled_date: date, send_hour_local: Number(hour) });
    onChange(e); setSchedEditing(false); toast({ title: "Schedule updated" });
  };

  const skipped = step.status === "skipped";
  const dt = step.scheduled_datetime;
  const recipientTime = dt ? fmtInTz(dt, step.timezone) : "";
  const myTime = dt ? fmtInTz(dt, MY_TX_SAFE(step)) : "";

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center shrink-0">
        <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[12px] font-semibold ${step.is_reply ? "bg-blue-500 text-white" : step.status === "sent" ? "bg-emerald-500 text-white" : step.status === "approved" ? "bg-blue-500 text-white" : skipped ? "bg-[hsl(240,5%,88%)] text-[hsl(240,4%,50%)]" : "brand-bg text-white"}`}>{step.is_reply ? <Reply className="h-3.5 w-3.5" /> : step.order}</div>
        <div className="w-px flex-1 bg-[hsl(240,6%,88%)] mt-1" />
      </div>
      <div className={`flex-1 rounded-xl border ${step.is_reply ? "border-blue-200" : "border-[hsl(240,6%,91%)]"} bg-white overflow-hidden mb-3 ${skipped ? "opacity-60" : ""}`}>
        <div className="flex items-center gap-2 px-4 py-2 border-b border-[hsl(240,6%,93%)] bg-[hsl(240,20%,99%)]">
          <Mail className="h-4 w-4 text-[hsl(245,50%,48%)]" />
          {step.is_reply && <span className="inline-flex rounded bg-blue-100 text-blue-700 px-1.5 py-0.5 text-[10px] font-medium">Suggested reply</span>}
          <span className={`ml-auto inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium ${stepStatusColor[step.status]}`}>{stepStatusLabel[step.status]}</span>
        </div>

        {/* Schedule row */}
        <div className="flex items-center gap-2 px-4 py-1.5 border-b border-[hsl(240,6%,94%)]">
          <CalendarClock className="h-3.5 w-3.5 text-[hsl(240,4%,55%)]" />
          {schedEditing ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded border border-[hsl(240,6%,88%)] px-1.5 py-0.5 text-[12px] outline-none" />
              <select value={hour} onChange={(e) => setHour(e.target.value)} className="rounded border border-[hsl(240,6%,88%)] px-1.5 py-0.5 text-[12px] outline-none">
                {Array.from({ length: 13 }, (_, i) => i + 6).map((h) => <option key={h} value={h}>{h}:00</option>)}
              </select>
              <span className="text-[11px] text-[hsl(240,4%,55%)]">their local ({tzAbbr(step.timezone)})</span>
              <button onClick={saveSched} className="rounded brand-bg text-white px-2 py-0.5 text-[11px] font-medium">Save</button>
              <button onClick={() => { setSchedEditing(false); setDate(step.scheduled_date); setHour(step.send_hour_local); }} className="text-[11px] text-[hsl(240,4%,45%)]">Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap text-[12px] text-[hsl(240,4%,50%)]">
              <span className="font-medium text-[hsl(240,6%,25%)]">{recipientTime}</span>
              <span>({tzAbbr(step.timezone)} · recipient)</span>
              <span className="text-[hsl(240,4%,70%)]">·</span>
              <span>{myTime} IST for you</span>
              {!skipped && step.status !== "sent" && (
                <button onClick={() => setSchedEditing(true)} className="text-[hsl(245,50%,50%)] hover:underline font-medium">Change</button>
              )}
            </div>
          )}
        </div>

        <div className="p-4">
          {editing ? (
            <>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full text-[14px] font-medium outline-none border-b border-[hsl(240,6%,90%)] pb-1.5 mb-2" />
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} className="w-full text-[13px] leading-relaxed text-[hsl(240,5%,25%)] outline-none resize-none whitespace-pre-wrap" />
              <div className="flex gap-2 mt-2">
                <button onClick={saveEdit} className="rounded-md brand-bg text-white px-2.5 py-1 text-[12px] font-medium">Save</button>
                <button onClick={() => { setEditing(false); setSubject(step.subject); setBody(step.body); }} className="rounded-md px-2.5 py-1 text-[12px] font-medium text-[hsl(240,4%,45%)] hover:bg-[hsl(240,5%,95%)]">Cancel</button>
              </div>
            </>
          ) : (
            <>
              <div className="text-[14px] font-medium">{step.subject}</div>
              <p className="text-[13px] leading-relaxed text-[hsl(240,5%,30%)] mt-1.5 whitespace-pre-wrap">{step.body}</p>
{step.reasoning && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-[hsl(245,58%,97%)] px-3 py-2">
                  <Sparkles className="h-3.5 w-3.5 text-[hsl(245,50%,48%)] mt-0.5 shrink-0" />
                  <span className="text-[12px] text-[hsl(245,30%,40%)]"><span className="font-medium">Hermes:</span> {step.reasoning}</span>
                </div>
              )}
              {step.last_error && step.status === "failed" && (
                <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-700">Send error: {step.last_error}</div>
              )}
              {step.resend_email_id && step.status === "sent" && (
                <div className="mt-2 text-[11px] text-[hsl(240,4%,55%)] font-mono">Resend id: {step.resend_email_id}</div>
              )}
            </>
          )}
        </div>
        {!editing && !skipped && step.status !== "sent" && (
          <div className="flex items-center gap-2 px-4 py-2 border-t border-[hsl(240,6%,93%)]">
            <button disabled={busy} onClick={() => setEditing(true)} className="text-[12px] font-medium text-[hsl(240,4%,45%)] hover:text-[hsl(240,6%,10%)]">Edit copy</button>
            {step.status === "pending_approval" && (
              <button disabled={busy} onClick={() => doAction(() => api.approveStep(enrollmentId, step.id), "Approved — will auto-send at scheduled time")} className="inline-flex items-center gap-1 rounded-md border border-[hsl(240,6%,88%)] px-2.5 py-1 text-[12px] font-medium hover:bg-[hsl(240,5%,96%)]"><Check className="h-3.5 w-3.5" /> Approve &amp; schedule</button>
            )}
<button disabled={busy} onClick={async () => {
              setBusy(true);
              try {
                const e = await api.sendStep(enrollmentId, step.id);
                onChange(e);
                toast({ title: "Sent via Resend" });
              } catch (err) {
                const detail = err?.response?.data?.detail;
                const enr = detail && typeof detail === "object" ? detail.enrollment : null;
                if (enr) onChange(enr);
                toast({ title: "Send failed", description: (detail && detail.message) || detail || "Could not send", variant: "destructive" });
              } finally { setBusy(false); }
            }} className="ml-auto inline-flex items-center gap-1 rounded-md brand-bg text-white px-2.5 py-1 text-[12px] font-medium hover:brightness-105">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />} Send now
            </button>
          </div>
        )}
        {step.status === "approved" && (
          <div className="px-4 py-1.5 border-t border-[hsl(240,6%,93%)] text-[11.5px] text-emerald-600 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Approved — auto-sends at the scheduled time above</div>
        )}
      </div>
    </div>
  );
}

function MY_TX_SAFE(step) { return MY_TZ; }

export default function SequenceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [seq, setSeq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEnr, setSelectedEnr] = useState(null);
  const [showEnroll, setShowEnroll] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = () => api.sequence(id).then((s) => {
    setSeq(s);
    setSelectedEnr((prev) => (s.enrollments || []).find((e) => e.id === prev?.id) || (s.enrollments || [])[0] || null);
  }).finally(() => setLoading(false));

  // load is intentionally recreated with the route state; only reload when the record id changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (loading) return <Spinner />;
  if (!seq) return <div className="p-6 text-[13px]">Sequence not found.</div>;

  const toggle = async () => {
    const next = seq.status !== "Active";
    const u = await api.activateSequence(seq.id, next);
    setSeq({ ...seq, status: u.status });
    toast({ title: `Sequence ${next ? "activated" : "paused"}` });
  };

  const saveSequence = async (values) => {
    const updated = await api.updateSequence(seq.id, values);
    setSeq((current) => ({ ...current, ...updated }));
    setShowEdit(false);
    toast({ title: "Sequence updated", description: updated.name });
  };

  const deleteSequence = async () => {
    setDeleteBusy(true);
    try {
      await api.deleteSequence(seq.id);
      toast({ title: "Sequence deleted", description: seq.name });
      navigate("/sequences");
    } catch (error) {
      toast({ title: "Could not delete sequence", description: error?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleteBusy(false);
    }
  };

  const onStepChange = (updatedEnr) => {
    setSelectedEnr(updatedEnr);
    setSeq((s) => ({ ...s, enrollments: s.enrollments.map((e) => (e.id === updatedEnr.id ? updatedEnr : e)) }));
  };

  const regenerate = async () => {
    const e = await api.regenerate(selectedEnr.id);
    onStepChange(e); toast({ title: "Hermes re-drafted the sequence" });
  };
  const markReplied = async () => {
    const e = await api.markReplied(selectedEnr.id);
    onStepChange(e); toast({ title: "Marked as replied", description: "Remaining steps halted" });
  };

  const enrollments = seq.enrollments || [];
  const enrolledIds = enrollments.map((e) => e.person_id);

  return (
    <div className="h-full flex flex-col animate-in-up">
      <div className="px-4 py-3 border-b border-[hsl(240,6%,92%)]">
        <button onClick={() => navigate("/sequences")} className="inline-flex items-center gap-1 text-[13px] text-[hsl(240,4%,50%)] hover:text-[hsl(240,6%,10%)] mb-2"><ArrowLeft className="h-4 w-4" /> Sequences</button>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg brand-bg-soft flex items-center justify-center shrink-0"><Send className="h-[18px] w-[18px] text-[hsl(245,50%,48%)]" /></div>
          <div>
            <div className="flex items-center gap-2"><h1 className="text-[18px] font-semibold">{seq.name}</h1><span className={`inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium ${seqStatusColor[seq.status]}`}>{seq.status}</span></div>
            <div className="text-[12.5px] text-[hsl(240,4%,50%)]">{seq.goal} · {seq.tone}</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setShowEnroll(true)} className="inline-flex items-center gap-1.5 rounded-md brand-bg text-white px-3 py-1.5 text-[13px] font-medium hover:brightness-105"><Plus className="h-4 w-4" /> Enroll person</button>
            <button onClick={toggle} className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(240,6%,88%)] px-3 py-1.5 text-[13px] font-medium hover:bg-[hsl(240,5%,96%)]">{seq.status === "Active" ? <><Pause className="h-4 w-4" /> Pause</> : <><Play className="h-4 w-4" /> Activate</>}</button>
            <ActionMenu onEdit={() => setShowEdit(true)} onDelete={() => setShowDelete(true)} />
          </div>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Enrollments list */}
        <div className="w-[320px] shrink-0 border-r border-[hsl(240,6%,92%)] overflow-auto">
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[hsl(240,4%,55%)]">Enrolled ({enrollments.length})</div>
          {enrollments.length === 0 && <div className="px-4 py-8 text-[13px] text-[hsl(240,4%,55%)]">Nobody enrolled yet. Click "Enroll person" and Hermes will draft a tailored cadence.</div>}
          {enrollments.map((e) => {
            const pending = e.steps.filter((s) => s.status === "pending_approval").length;
            return (
              <button key={e.id} onClick={() => setSelectedEnr(e)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left border-l-2 ${selectedEnr?.id === e.id ? "bg-[hsl(240,20%,99%)] border-[hsl(245,58%,60%)]" : "border-transparent hover:bg-[hsl(240,20%,99%)]"}`}>
                <Avatar text={e.person_name.split(" ").map((w) => w[0]).join("")} brand size={30} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{e.person_name}</div>
                  <div className="text-[12px] text-[hsl(240,4%,50%)] truncate">{e.company}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[10.5px] font-medium ${enrStatusColor[e.status]}`}>{e.status}</span>
                  {pending > 0 && <span className="text-[10.5px] text-amber-600 font-medium">{pending} to review</span>}
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected enrollment steps */}
        <div className="flex-1 overflow-auto">
          {!selectedEnr ? (
            <div className="h-full flex items-center justify-center text-[13px] text-[hsl(240,4%,55%)]">Select someone to see their personalized sequence.</div>
          ) : (
            <div className="p-5 max-w-[720px] mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <Avatar text={selectedEnr.person_name.split(" ").map((w) => w[0]).join("")} brand size={40} />
                <div className="flex-1">
                  <div className="text-[16px] font-semibold">{selectedEnr.person_name}</div>
                  <div className="text-[12.5px] text-[hsl(240,4%,50%)]">{selectedEnr.company} · {selectedEnr.steps.length} personalized touches</div>
                </div>
                <button onClick={regenerate} className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(240,6%,88%)] px-2.5 py-1.5 text-[12.5px] font-medium hover:bg-[hsl(240,5%,96%)]"><RotateCcw className="h-3.5 w-3.5" /> Re-draft</button>
                {selectedEnr.status !== "replied" && (
                  <button onClick={markReplied} className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(240,6%,88%)] px-2.5 py-1.5 text-[12.5px] font-medium hover:bg-[hsl(240,5%,96%)]"><Reply className="h-3.5 w-3.5" /> Mark replied</button>
                )}
              </div>
              {selectedEnr.status === "replied" && (
                <div className="mb-4 flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-[12.5px] text-blue-700"><Reply className="h-4 w-4" /> This person replied — remaining steps were halted automatically.</div>
              )}
              {selectedEnr.steps.map((step) => (
                <StepCard key={step.id} step={step} enrollmentId={selectedEnr.id} onChange={onStepChange} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showEnroll && (
        <EnrollModal sequenceId={seq.id} enrolledIds={enrolledIds} onClose={() => setShowEnroll(false)} onEnrolled={(enr) => { setShowEnroll(false); setSeq((s) => ({ ...s, enrollments: [...(s.enrollments || []), enr] })); setSelectedEnr(enr); }} />
      )}
      <SequenceForm open={showEdit} onClose={() => setShowEdit(false)} onSubmit={saveSequence} initialValues={seq} />
      <ConfirmDialog open={showDelete} onClose={() => !deleteBusy && setShowDelete(false)} onConfirm={deleteSequence} busy={deleteBusy} title="Delete sequence?" description={`“${seq.name}” and its enrollments will be permanently removed.`} />
    </div>
  );
}
